import { CircularProgress, useTheme } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { customStyles as customStylesFlag } from '@portal/config/app.json';
import useReduxState from '@portal/hooks/useReduxState';
import React from 'react';

type ButtonColor = 'primary' | 'secondary' | 'default' | 'inherit' | 'error';
type ButtonVariant = 'text' | 'outlined' | 'contained';

interface AdvancedButtonProps {
  type?: 'button' | 'submit' | 'reset';
  size?: 'small' | 'medium' | 'large';
  color?: ButtonColor;
  variant?: ButtonVariant;
  textColor?: string;
  text: string;
  onClick?: () => void;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  fullWidth?: boolean;
  disabled?: boolean;
  disableElevation?: boolean;
  customStyles?: React.CSSProperties;
  height?: string | number;
  hoverColor?: string;
  testId?: string;
  className?: string;
  loading?: boolean;
  ariaLabel?: string;
  dataTestId?: string;
  useSystemColor?: boolean;
}

interface VariantStyle {
  background?: string;
  border: string;
  textColor: string;
}

interface PaletteShape {
  primary: { main: string; contrastText: string };
  secondary: { main: string };
  error: { main: string; contrastText: string };
}

function getVariantStyle(
  color: ButtonColor,
  variant: ButtonVariant,
  palette: PaletteShape,
  themeMain: string | undefined,
  themeBorder: string | undefined,
  useSystemColor: boolean,
  fallbackTextColor: string
): VariantStyle {
  const isContained = variant === 'contained';
  const isOutlined = variant === 'outlined';
  if (color === 'error') {
    const main = palette.error.main;
    const contrast = palette.error.contrastText;
    return {
      background: isContained ? main : undefined,
      border: isOutlined ? `2px solid ${main}` : 'none',
      textColor: isContained ? contrast : main,
    };
  }
  if (color === 'primary' && (themeMain || !useSystemColor)) {
    const main = useSystemColor
      ? themeMain ?? palette.primary.main
      : palette.primary.main;
    const contrast = palette.primary.contrastText;
    return {
      background: isContained ? main : undefined,
      border: isOutlined ? `2px solid ${main}` : 'none',
      textColor: isContained ? contrast : main,
    };
  }
  if (color === 'secondary' && (themeBorder || !useSystemColor)) {
    const main = useSystemColor
      ? themeBorder ?? palette.secondary.main
      : palette.secondary.main;
    return {
      background: isContained ? main : undefined,
      border: isOutlined ? `2px solid ${main}` : 'none',
      textColor: isContained ? 'white' : main,
    };
  }
  return {
    border: isOutlined ? `2px solid ${palette.primary.main}` : 'none',
    textColor: fallbackTextColor,
  };
}

const AdvancedButton: React.FC<AdvancedButtonProps> = ({
  type = 'button',
  size = 'large',
  color = 'primary',
  variant = 'contained',
  textColor = 'black',
  text,
  onClick,
  startIcon,
  endIcon,
  fullWidth = false,
  disabled = false,
  disableElevation = false,
  customStyles = {},
  height,
  testId = '',
  className = '',
  loading = false,
  ariaLabel,
  dataTestId,
  useSystemColor = true,
}) => {
  const { palette } = useTheme();
  const {
    theme: { currentTheme },
  } = useReduxState();

  const fallbackTextColor = customStylesFlag.darkMode
    ? (palette as unknown as { grey?: Record<string, string> }).grey?.[50] ??
      'black'
    : textColor;

  const variantStyle = getVariantStyle(
    color,
    variant,
    {
      primary: palette.primary,
      secondary: palette.secondary,
      error: palette.error,
    },
    currentTheme?.primaryColor,
    currentTheme?.borderColor,
    useSystemColor,
    fallbackTextColor
  );

  const buttonStyles: React.CSSProperties = {
    borderRadius: 12,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    padding: '12px 24px',
    minHeight: '48px',
    transition:
      'box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow:
      variant === 'contained' ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
    border: variantStyle.border,
    ...(variantStyle.background && {
      background: variantStyle.background,
    }),
    ...customStyles,
  };

  const textStyles: React.CSSProperties = {
    color: variantStyle.textColor,
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: 1.4,
  };

  const loadingSpinnerStyles: React.CSSProperties = {
    color: variantStyle.textColor,
    marginLeft: '8px',
    width: '16px',
    height: '16px',
  };

  const iconContainerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    marginRight: '8px',
  };

  const endIconContainerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    marginLeft: '8px',
  };

  const buttonContentStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
  };

  const handleClick = () => {
    if (onClick && !loading && !disabled) {
      onClick();
    }
  };

  const buttonAriaLabel = ariaLabel || text;
  const muiColor = color === 'error' ? 'default' : color;

  return (
    <div className="advanced-button">
      <Button
        style={{ height, ...buttonStyles }}
        size={size}
        color={muiColor}
        variant={variant}
        onClick={handleClick}
        fullWidth={fullWidth}
        type={type}
        disabled={disabled || loading}
        disableElevation={disableElevation}
        aria-label={buttonAriaLabel}
        data-testid={dataTestId || testId}
        role="button"
        tabIndex={disabled ? -1 : 0}
        {...{ testId }}
      >
        <div style={buttonContentStyles}>
          {!loading && startIcon ? (
            <span style={iconContainerStyles} aria-hidden="true">
              {startIcon}
            </span>
          ) : null}

          <span style={textStyles} className={className}>
            {text}
          </span>

          {!loading && endIcon ? (
            <span style={endIconContainerStyles} aria-hidden="true">
              {endIcon}
            </span>
          ) : null}

          {loading ? (
            <CircularProgress
              size={16}
              style={loadingSpinnerStyles}
              aria-label="Loading"
            />
          ) : null}
        </div>
      </Button>
    </div>
  );
};

export default AdvancedButton;
